import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { FiChevronRight } from 'react-icons/fi';
import isInert from '@repo/utils/isInert';
import { LocaleContext } from '@repo/contexts/LocaleContext';
import { SiteSettingsContext } from '@repo/contexts/SiteSettingsContext';
import { LocalizedLink } from '@repo/gatsby-localization-helpers';
import { useUiComponentsInternationalContext } from '@repo/static-translations';
import type { GetInternalLinkFunctionType } from '@repo/models/GetInternalLinkFunctionType';
import type {
  SanityLink,
  SanityBlockSvg,
} from '@repo/models/gatsby-graphql-types';
import sharedStyles from '@repo/ui-components/styles';
import breakpoints from '@repo/ui-components/breakpoints';
import PhoneIcon from '@repo/ui-components/svgs/phone-icon.svg';
import ChatIcon from '@repo/ui-components/svgs/chat-icon.svg';
import CountryPicker from '@repo/ui-components/notification-banner/CountryPicker';
import UploadedSvgIcon from '@repo/ui-components/notification-banner/UploadedSvgIcon';
// eslint-disable-next-line import/no-cycle
import DealerSearchTrigger from '@repo/ui-components/dealer-search/DealerSearchTrigger';

const Container = styled.div<{ $sticky: boolean; $showShadow: boolean }>`
  min-height: 30px;
  height: 32px;
  display: flex;
  background: ${({ theme }) => theme?.notificationBanner?.background};
  transition: box-shadow 0.3s ease-in-out;
  position: ${({ $sticky }) => ($sticky ? 'sticky' : 'relative')};
  box-shadow: ${({ $showShadow }) =>
    $showShadow ? '0 2px 4px 0 rgba(0, 0, 0, 0.35)' : 'none'};
  z-index: 3;
  top: 0;
  color: ${({ theme }) => theme?.notificationBanner?.text ?? 'white'};
  font-weight: normal;
  font-size: ${sharedStyles.fontSize.sm};

  a {
    color: ${({ theme }) => theme?.notificationBanner?.text ?? 'white'};
    font-weight: normal;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const MessageContainer = styled.div`
  width: 100%;
  height: 100%;
`;

const CenterMessage = styled.div`
  max-width: 1280px;
  height: 100%;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  @media (max-width: ${breakpoints.md}) {
    width: 100%;
  }
`;

const Message = styled.div`
  display: flex;
  padding: ${sharedStyles.spacing['spacing-1xs']} 0;
  justify-content: center;

  @media (max-width: ${breakpoints.md}) {
    width: 100%;
  }

  a {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const CountryPickerContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 0 ${sharedStyles.spacing['spacing-md']};
  height: 100%;

  @media (min-width: ${breakpoints.lg}) {
    display: none;
  }
`;

const SupportAreaContainer = styled.div`
  max-width: 1280px;
  width: 100%;
  height: 100%;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`;

const SupportArea = styled.div`
  display: none;
  width: 30%;
  min-width: 260px;
  right: 0;
  position: absolute;
  padding: 0 ${sharedStyles.spacing['spacing-md']} 0 0;
  @media (min-width: ${breakpoints.lg}) {
    display: flex;
    justify-content: flex-end;
    padding: 0 1rem 0 0;
  }
`;

const ChatContainer = styled.div`
  padding: ${sharedStyles.spacing['spacing-1xs']} 0;

  display: flex;
  align-items: center;
  margin-right: ${sharedStyles.spacing['spacing-sm']};

  svg {
    margin-top: -2px;
    margin-right: ${sharedStyles.spacing['spacing-1xs']};
  }
`;

const PhoneContainer = styled.div`
  a {
    padding: ${sharedStyles.spacing['spacing-1xs']} 0;
    display: flex;
    align-items: center;
  }

  svg {
    margin-top: -2px;
    margin-right: ${sharedStyles.spacing['spacing-1xs']};
  }
`;

const ChevContainer = styled.div`
  margin-left: ${sharedStyles.spacing['spacing-1xs']};

  svg {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

function DemoChatComponent() {
  const { tt } = useUiComponentsInternationalContext();

  return (
    <div>
      <div>{tt(s => s.notificationBannerCommon.liveChat)}</div>
    </div>
  );
}

function debounce(method, delay) {
  const methodToDebounce = method;

  clearTimeout(methodToDebounce.timeoutId);

  methodToDebounce.timeoutId = setTimeout(() => {
    method();
  }, delay);
}

const DealerSearchTriggerContainer = styled.div`
  display: none;
  width: 30%;
  min-width: 260px;
  left: 0;
  position: absolute;
  justify-content: flex-start;
  @media (min-width: ${breakpoints.lg}) {
    display: flex;
  }
`;

export type NotificationBannerProps = {
  messageText?: string;
  messageLink?: string;
  messageInternalLink?: SanityLink['internalLink'];
  ChatComponent?: React.FC;
  messageType?: string;
  messageSvg?: SanityBlockSvg;
  /**
   * Code-driven icon override (e.g. a `lucide-react` icon) for a
   * non-Sanity-managed banner message — takes precedence over `messageSvg`,
   * which only renders an uploaded Sanity SVG asset.
   */
  messageIcon?: React.ReactNode;
  /** Fired when the banner's message link is clicked, for analytics. */
  onMessageLinkClick?: () => void;
  sticky?: boolean;
  getInternalLink: GetInternalLinkFunctionType;
  hasDealerSearch: boolean;
  hideFromScreenReader?: boolean;
};

function NotificationBanner({
  messageText: messageTextProp,
  messageLink = '/',
  messageInternalLink,
  ChatComponent = DemoChatComponent,
  messageType = 'External',
  messageSvg,
  messageIcon,
  onMessageLinkClick,
  sticky = false,
  getInternalLink = () => '',
  hasDealerSearch = false,
  hideFromScreenReader = false,
}: Readonly<NotificationBannerProps>) {
  const localeContext = useContext(LocaleContext);
  const { siteSettings } = useContext(SiteSettingsContext);

  const { tt } = useUiComponentsInternationalContext();
  const messageText =
    messageTextProp ??
    tt(s => s.notificationBannerCommon.replaceWithMessageFromBackendMessage);
  const internalMessageHref =
    messageType === 'internal' && messageInternalLink
      ? getInternalLink({
          reference: messageInternalLink,
          currency: localeContext?.currency,
        })
      : '';
  const hasMessageLink =
    messageType === 'internal'
      ? Boolean(messageInternalLink && internalMessageHref)
      : Boolean(messageLink);

  const [showShadow, setShowShadow] = useState(false);

  const isBrowser = typeof window !== 'undefined';

  // debounce scroll event
  if (isBrowser) {
    window.onscroll = () => {
      debounce(() => {
        if (window.scrollY > 0 && sticky) {
          setShowShadow(true);
        }
        if (window.scrollY === 0 && sticky) {
          setShowShadow(false);
        }
      }, 100);
    };
  }

  const getUploadIconIfNeeded = () => {
    if (messageIcon) return messageIcon;
    if (messageSvg?.linkType === 'upload' && messageSvg?.svgUploaded !== null) {
      return (
        <UploadedSvgIcon
          url={messageSvg?.svgUploaded?.asset?.url}
          alt={messageSvg?.svgUploaded?.alt}
        />
      );
    }
    return null;
  };

  return (
    <Container
      $sticky={sticky}
      $showShadow={showShadow}
      // @ts-ignore
      inert={isInert(hideFromScreenReader)}
    >
      <MessageContainer>
        <CenterMessage>
          {hasMessageLink ? (
            <Message>
              {messageType === 'internal' && messageInternalLink && (
                <LocalizedLink to={internalMessageHref} onClick={onMessageLinkClick}>
                  {getUploadIconIfNeeded()}
                  {messageText}
                  <ChevContainer>
                    <FiChevronRight size={20} strokeWidth={1.2} />
                  </ChevContainer>
                </LocalizedLink>
              )}
              {messageType !== 'internal' && (
                <a href={messageLink} onClick={onMessageLinkClick}>
                  {getUploadIconIfNeeded()}
                  {messageText}
                  {messageType !== 'disable' ? (
                    <ChevContainer>
                      <FiChevronRight size={20} strokeWidth={1.2} />
                    </ChevContainer>
                  ) : (
                    ''
                  )}
                </a>
              )}
            </Message>
          ) : (
            <span>{messageText}</span>
          )}
        </CenterMessage>

        {localeContext?.currency && (
          <CountryPickerContainer>
            <CountryPicker active={localeContext?.countryPickerActive} />
          </CountryPickerContainer>
        )}
      </MessageContainer>
      <SupportAreaContainer>
        {hasDealerSearch && (
          <DealerSearchTriggerContainer>
            <DealerSearchTrigger />
          </DealerSearchTriggerContainer>
        )}
        <SupportArea>
          {ChatComponent && (
            <ChatContainer>
              <ChatIcon fill="currentColor" width={16} height={16} />
              <ChatComponent />
            </ChatContainer>
          )}
          <PhoneContainer>
            <a href={`tel:${siteSettings.phone.replace(/\D/g, '')}`}>
              <PhoneIcon fill="currentColor" width={16} height={16} />
              <div>{siteSettings.phone}</div>
            </a>
          </PhoneContainer>
          {localeContext?.currency && (
            <CountryPicker active={localeContext?.countryPickerActive} />
          )}
        </SupportArea>
      </SupportAreaContainer>
    </Container>
  );
}
export default NotificationBanner;
